import { isDefinedArray, isPositiveNumber, reduce } from "et3-type-guards";
import { memoize } from "lodash";
import { configure, observable } from "mobx";

import { apiCallGetAllAdmins } from "../../api/core-api/api-call-get-all-admins";
import et3ReadableDate, {
  getHoursDifference
} from "../../helpers/et3-date-helper";
import {
  getPermissionIDRequired,
  hasSpecificRoutePermission
} from "../../helpers/et3-has-permission";
import getCoreConnectionStore from "./core-connection-store";
import getCoreHistoryStore from "./core-history-store";
import getCoreLoginStore from "./core-login-store";
import getCorePersonalStore from "./core-personal-store";
import getCorePopUpStore from "./core-popup-store";
import getCoreTabStore from "./core-tab-store";
import getCoreTimingStore from "./core-timing-store";
import getCoreStorageStore from "./core-storage-store";
import getCoreUIStore from "./core-ui-store";
import getDashBoardStore from "../dashboard-store";
import { getNotificationSoundStore } from "../notification-store/notification-sound-store";
import { SESSION_HOURS_TIME_LIMIT } from "../../constants/core-constants";
import {
  E_LOCAL_STORAGE_KEY,
  E_SESSION_STORAGE_KEY,
  E_STORAGE_TYPE
} from "../../constants/storage-constants";
import { TAdmin } from "../../Types";

class RootStore {
  allAdmins = observable.box<{ value: string; label: string }[]>([]);

  startApp = async () => {
    this.setMobxConfig();

    const userFromStorage = getCoreStorageStore(
      E_STORAGE_TYPE.LOCAL
    ).get<TAdmin>(E_LOCAL_STORAGE_KEY.USER);
    const userPermissionFromStorage = getCoreStorageStore(
      E_STORAGE_TYPE.LOCAL
    ).get<number[]>(E_LOCAL_STORAGE_KEY.USER_PERMISSIONS);

    getNotificationSoundStore().setIsSoundEnabledFromStorage();

    if (isPositiveNumber(userFromStorage.id)) {
      const user = reduce(() => userFromStorage, {
        id: -1,
        username: "",
        fullName: "",
        role: "",
        lastLogin: et3ReadableDate(Date.now()),
        needResetPassword: false
      });
      const permissions = reduce(() => userPermissionFromStorage, []);
      const dateDifferenceInHours = getHoursDifference({
        dateFrom: new Date(user.lastLogin),
        dateTo: new Date()
      });

      getCorePersonalStore().user.set(user);
      getCorePersonalStore().userPermissions.set(permissions);
      getCorePersonalStore().setUserProfilePic();

      const isAnyKhattabaAdmin =
        getCorePersonalStore().isAnyKhattabaAdmin.get();
      let accessState = true;
      // For testing session 1 min
      // if (dateDifferenceInMinutes > 1) {
      const isSessionTimeExceeded =
        dateDifferenceInHours > SESSION_HOURS_TIME_LIMIT;
      if (isSessionTimeExceeded || !accessState) {
        getCorePopUpStore().showAlertMessage("SESSION_EXPIRED");
        await getCoreLoginStore().signOutHandler();
        return false;
      }

      getCoreUIStore().isSideBarVisible.set(false);
      this.initializeTimersAndListenners();
      if (!isAnyKhattabaAdmin) {
        await this.setDashboardCounts();
      } else {
        await getDashBoardStore().getKhattabatDashboardData();
        await getCorePersonalStore().getAdminVersionStatesFromDatabase();
      }

      if (getCorePersonalStore().user.get().needResetPassword) {
        getCorePopUpStore().openUpdatePasswordPopUp();
      }

      return true;
    } else {
      getCorePersonalStore().user.set({
        id: -1,
        username: "",
        fullName: "",
        role: "",
        lastLogin: "",
        needResetPassword: false
      });
      getCoreUIStore().isSideBarVisible.set(false);
      return false;
    }
  };

  setMobxConfig = () => {
    //this config is to remove the warns "Since strict-mode is enabled,
    //changing (observed) observable values without using an action is not allowed"
    configure({
      enforceActions: "never"
    });
  };

  refreshData = async () => {
    const isAnyKhattabaAdmin = getCorePersonalStore().isAnyKhattabaAdmin.get();
    if (isAnyKhattabaAdmin) {
      await getCoreTimingStore().refreshKhattabatPageData(
        getCoreHistoryStore().getPageName()
      );
      return;
    }
    await getCorePersonalStore().getAdminVersionStatesFromDatabase();
    getCoreUIStore().setIsLoading("ALARMS", true);
    getCoreUIStore().setLatestDataLoading(true);
    await getCorePersonalStore().getMyPermission();
    const userPermissions = getCorePersonalStore().userPermissions.get();
    const hasDashboardPermission =
      userPermissions.indexOf(getPermissionIDRequired("Alarms")) > -1;
    if (hasDashboardPermission) {
      await getDashBoardStore().getDashboardData();
    }
    getCoreUIStore().setIsLoading("ALARMS", false);
    getCoreUIStore().setLatestDataLoading(false);
  };

  setDashboardCounts = async () => {
    const isAnyKhattabaAdmin = getCorePersonalStore().isAnyKhattabaAdmin.get();
    if (isAnyKhattabaAdmin) {
      return;
    }
    await this.refreshData();
    const counts = getCoreStorageStore(E_STORAGE_TYPE.SESSION).get<
      Record<string, number>
    >(E_SESSION_STORAGE_KEY.COUNTS);
    const reportsCount = reduce(() => counts.reportsCount, 0);
    const reportedCount = reduce(() => counts.reportedCount, 0);
    const approvalCount = reduce(() => counts.approvalCount, 0);
    const investigationCasesCount = reduce(
      () => counts.investigationCasesCount,
      0
    );

    const activeTime = reduce(() => counts.activeTime, 0);
    getDashBoardStore().reportsCount.set(reportsCount);
    getDashBoardStore().reportedCount.set(reportedCount);
    getDashBoardStore().approvalCount.set(approvalCount);
    getDashBoardStore().investigationCasesCount.set(investigationCasesCount);
    getDashBoardStore().activeTime.set(activeTime);
  };

  initializeTimersAndListenners = () => {
    const isAnyKhattabaAdmin = getCorePersonalStore().isAnyKhattabaAdmin.get();
    if (isAnyKhattabaAdmin) {
      getCoreTimingStore().turnOnKhattabaRefreshTimer();
    } else {
      getCoreTimingStore().turnOnRefreshTimer();
    }
    getCoreTabStore().onStartListeners();
    getCoreConnectionStore().onStartListeners();
  };

  killAppTimersAndListenners = () => {
    getCoreTimingStore().kilTimers();
    getCoreTabStore().onKillListeners();
    getCoreConnectionStore().onKillListeners();
  };
  getAllAdmins = async () => {
    if (isDefinedArray(this.allAdmins) && this.allAdmins.length > 0) {
      return;
    }
    const hasPermission = hasSpecificRoutePermission("ALL_ADMINS");
    if (!hasPermission) {
      const adminArray = [
        {
          label: getCorePersonalStore().user.get().fullName,
          value: getCorePersonalStore().user.get().id.toString()
        }
      ];
      const newAdminArray = adminArray.concat({
        value: "",
        label: "Choose..."
      });
      this.allAdmins.set(newAdminArray);
      return;
    }
    const { data, status } = await apiCallGetAllAdmins();
    if (status === "OK") {
      const newData = data.map((item) => {
        return { value: item.adminId.toString(), label: item.fullname };
      });
      const newArray = newData.concat({ value: "", label: "Choose..." });
      this.allAdmins.set(newArray);
    }
  };
}
const getRootStore = memoize(
  () => new RootStore(),
  () => 1
);
export default getRootStore;
